import { useCallback, useEffect, useState } from "react";
import { k, readJSON, writeJSON } from "@/lib/storage";

export type Theme = "light" | "dark";

const KEY = k("theme");

function getInitial(): Theme {
  const stored = readJSON<Theme | null>(KEY, null);
  if (stored === "light" || stored === "dark") return stored;
  const fromDOM = document.documentElement.getAttribute("data-theme");
  return fromDOM === "dark" ? "dark" : "light";
}

/**
 * Theme is applied via the `data-theme` attribute on <html>, which the CSS
 * tokens use to swap palettes. Persists across reloads.
 */
export function useTheme(): { theme: Theme; toggle: () => void; setTheme: (t: Theme) => void } {
  const [theme, setThemeState] = useState<Theme>(getInitial);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    writeJSON(KEY, theme);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggle = useCallback(() => setThemeState((t) => (t === "dark" ? "light" : "dark")), []);

  return { theme, toggle, setTheme };
}
